import type { AxisFilterChip, AxisFilterFieldDef } from "@/components/filter";
import type { TrainingEvalRunFilters } from "@/api/trainingEval";
import { evalRunStatusLabel } from "./evalRunFormat";

export type EvalRunFilterFieldDef = AxisFilterFieldDef<keyof TrainingEvalRunFilters>;

// 서버 조회 값은 raw status 그대로다. 표시 카테고리(대기·진행 중)가 아니라 이 값으로 거른다.
const STATUS_FILTER_VALUES = ["pending", "running", "succeeded", "failed", "cancelled"] as const;

export const EVAL_RUN_FILTER_FIELDS = [
  // 표 첫 열이 정책 이름이므로 가장 자주 찾는 축이다. 배열의 첫 원소가 기본 선택 축이다.
  { key: "policyName", label: "Policy 이름" },
  { key: "session", label: "Session" },
  { key: "branch", label: "Branch" },
  { key: "hash", label: "Hash" },
  { key: "dataset", label: "Dataset" },
  { key: "task", label: "Task" },
  { key: "dockerTag", label: "Docker Tag" },
  {
    key: "status",
    label: "상태",
    // 옵션 라벨은 표의 상태 배지와 같은 단어여야 한다. 그래야 칩과 셀이 같은 용어를 쓴다.
    options: STATUS_FILTER_VALUES.map((value) => ({ value, label: evalRunStatusLabel(value) })),
  },
] as const satisfies readonly EvalRunFilterFieldDef[];

// 칩으로 만들 수 있는 축은 위에 선언한 것뿐이다. TrainingEvalRunFilters 에 페이지 축(workspace,
// experiment 등)이 추가되어도 칩 축은 늘지 않는다. 그 축들은 칩이 아니라 드롭다운이 소유한다.
export type EvalRunFilterField = (typeof EVAL_RUN_FILTER_FIELDS)[number]["key"];

export type EvalRunFilterChip = AxisFilterChip<EvalRunFilterField>;

export function isEvalRunFilterField(key: string): key is EvalRunFilterField {
  return EVAL_RUN_FILTER_FIELDS.some((field) => field.key === key);
}

/**
 * 필터 칩을 서버 조회 필터로 합친다.
 *
 * 한 필드가 여러 번 나오면 마지막 값을 쓴다.
 *
 * Args:
 *   chips: 활성 필터 칩.
 *
 * Returns:
 *   서버 조회 필터.
 */
export function chipsToFilters(chips: readonly EvalRunFilterChip[]): TrainingEvalRunFilters {
  const filters: TrainingEvalRunFilters = {};
  for (const chip of chips) {
    const value = chip.value.trim();
    if (value !== "") {
      filters[chip.key] = value;
    }
  }
  return filters;
}
